import * as babylon from "@babylonjs/core";

import { BabylonState } from "../../state";

import { logTimeBinarySearch } from "./setPositionsToTime";

/** A rotation cache: parallel arrays of sample times and orientations. */
export interface SampledRotations {
  logtimes: bigint[];
  allRotations: babylon.Quaternion[];
}

/**
 * The cached orientation at `time`, interpolating between bracketing samples.
 *
 * Slerp rather than lerp: lerping quaternion components takes a chord through
 * the unit sphere rather than an arc along it, which reads as a rotation that
 * slows in the middle and, past a quarter turn, visibly wrong.
 *
 * Clamps outside the cache's range, as `localPositionAt` does — before the first
 * sample yields the first orientation, after the last yields the last. Always
 * allocates, so a caller can compose into the result without rewriting the
 * history it is reading.
 */
export function localRotationAt(
  cache: SampledRotations,
  time: bigint,
): babylon.Quaternion | undefined {
  const index = logTimeBinarySearch(cache.logtimes, time);
  const lastTime = cache.logtimes.at(index);
  const lastRotation = cache.allRotations.at(index);
  if (lastTime === undefined || lastRotation === undefined) {
    return undefined;
  }

  const nextTime = cache.logtimes.at(index + 1);
  const nextRotation = cache.allRotations.at(index + 1);
  if (nextTime === undefined || nextRotation === undefined) {
    return lastRotation.clone();
  }

  // Guarded, unlike the position path: duplicate sample times are avoided by
  // construction upstream, but a zero span here would make the fraction NaN and
  // poison every descendant's orientation through the composition.
  const span = Number(nextTime - lastTime);
  const amount =
    span === 0 ? 0 : Math.min(Math.max(Number(time - lastTime) / span, 0), 1);
  return babylon.Quaternion.Slerp(lastRotation, nextRotation, amount);
}

/**
 * Sets every entity's current orientation to the visualized time.
 *
 * The rotational counterpart of `updatePositions`, and run beside it, before the
 * hierarchy is composed — `resolveGlobalPositions` reads what this writes.
 */
export function updateRotations(state: BabylonState) {
  const time = state.sceneConfig.time;
  if (time === undefined) {
    return;
  }
  for (const entity of Object.values(state.entities)) {
    const rotation = entity.rotation;
    if (!rotation || rotation.cache.load.state !== "loaded") {
      continue;
    }
    const sampled = localRotationAt(rotation.cache, time);
    if (!sampled) {
      continue;
    }
    rotation.current.logtime = time;
    rotation.current.quaternion = sampled;
  }
}
